import React from "react"
import { Modal, Button, Typography, Flex } from "antd"
import { motion, AnimatePresence } from "framer-motion"
import * as hooks from "lib/hooks"

export interface MCCModalProps {
	open: boolean
	onClose: () => void
	title?: React.ReactNode
	message?: React.ReactNode
	cancelText?: string
	confirmText?: string
	onConfirm?: () => void
	width?: number | string
	maxBodyHeight?: number | string
	children?: React.ReactNode
	zIndex?: number
}

const modalContentContainerVariants = {
	hidden: { opacity: 1 },
	visible: {
		opacity: 1,
		transition: {
			delayChildren: 0.08,
			staggerChildren: 0.07,
		},
	},
}

const modalItemVariants = {
	hidden: { opacity: 0, y: 18 }, // Slightly more subtle start position
	visible: { opacity: 1, y: 0, transition: { type: "spring" as const, stiffness: 330, damping: 28 } }, // Responsive spring
}

const modalButtonMotionProps = (i: number) => ({
	variants: {
		hidden: { opacity: 0, y: 12 },
		visible: {
			opacity: 1,
			y: 0,
			transition: { delay: 0.12 + i * 0.06, type: "spring" as const, stiffness: 330, damping: 28 },
		},
	},
	whileHover: { scale: 1.03, transition: { duration: 0.15, ease: "easeOut" as const } },
	whileTap: { scale: 0.98, transition: { duration: 0.1, ease: "easeOut" as const } }, // Less drastic tap scale
	initial: "hidden",
	animate: "visible",
	style: { width: "100%" },
})

export function MCCModal({
	open,
	onClose,
	title = "Confirm Action",
	message,
	cancelText = "Cancel",
	confirmText = "Confirm",
	onConfirm,
	width = 400,
	maxBodyHeight = "70vh",
	children,
	zIndex = 20000,
}: MCCModalProps) {
	const { mode } = hooks.UseTheme()
	const { token: antdToken } = hooks.UseAntDTheme()
	const materialColors = hooks.UseMaterialColors()

	// Get Material You colors and tokens instead of using Ant Design tokens
	const surface = mode === "dark" ? materialColors.surface[1] : materialColors.surface[1]

	const onSurface = mode === "dark" ? materialColors.onSurface : materialColors.onSurface

	const onSurfaceVariant =
		mode === "dark" ? materialColors.onSurfaceVariant : materialColors.onSurfaceVariant

	const outlineColor = mode === "dark" ? materialColors.outline : materialColors.outline

	const primaryColor = materialColors.primary

	// Use Material You's corner radius for more Google-like appearance
	const cornerRadius = materialColors.shape.corner.large
	const buttonRadius = materialColors.shape.corner.full // Pill-shaped buttons

	// Material You elevation shadow
	const elevation =
		mode === "dark" ? materialColors.elevation.elevation5 : materialColors.elevation.elevation5

	const styledTitle = title ? (
		<Typography.Title
			level={5}
			style={{
				margin: 0,
				fontWeight: 600, // Strong but not heavy
				color: onSurface,
				textAlign: "center",
			}}
		>
			{title}
		</Typography.Title>
	) : null

	const renderFooter = () => {
		const showConfirm = confirmText && onConfirm
		const showCancel = cancelText
		if (!showConfirm && !showCancel) return null

		return (
			<motion.div
				variants={modalContentContainerVariants}
				initial="hidden"
				animate="visible"
				style={{ width: "100%" }}
			>
				<Flex vertical gap={16} style={{ width: "100%" }}>
					{showConfirm && (
						<motion.div {...modalButtonMotionProps(0)}>
							<Button
								type="primary"
								size="large"
								block
								onClick={onConfirm}
								style={{
									borderRadius: buttonRadius, // Google's pill-shaped buttons
									boxShadow: "none", // Flat Material button style
									fontWeight: 500,
									backgroundColor: primaryColor,
									borderColor: primaryColor,
									height: 40, // Slightly taller for Material You
								}}
							>
								{confirmText}
							</Button>
						</motion.div>
					)}
					{showCancel && (
						<motion.div {...modalButtonMotionProps(showConfirm ? 1 : 0)}>
							<Button
								type="text" // Use text button for secondary/cancel action
								size="large"
								block
								onClick={onClose}
								style={{
									borderRadius: buttonRadius,
									fontWeight: 500,
									color: primaryColor,
									height: 40, // Slightly taller for Material You
								}}
							>
								{cancelText}
							</Button>
						</motion.div>
					)}
				</Flex>
			</motion.div>
		)
	}

	const modalStyles: import("antd").ModalProps["styles"] = {
		mask: {
			backgroundColor: mode === "dark" ? "rgba(0,0,0,0.55)" : "rgba(0,0,0,0.45)",
			backdropFilter: "blur(4px)",
			WebkitBackdropFilter: "blur(4px)",
		},
		content: {
			padding: 0,
			borderRadius: cornerRadius,
			boxShadow: elevation,
			backgroundColor: surface,
			overflow: "hidden",
		},
		header: {
			backgroundColor: surface,
			padding: "24px 24px 16px", // Material You spacing
			margin: 0,
			borderBottom: `1px solid ${outlineColor}`,
			textAlign: "center",
		},
		body: {
			padding: "16px 24px", // Material You spacing
			overflowY: "auto",
			maxHeight: maxBodyHeight,
			// Added className to target with CSS for scrollbars
			className: `mcc-modal-body-scroll-${mode}`,
		} as any,
		footer: {
			padding: "16px 24px 24px", // Material You spacing
			marginTop: 0,
			borderTop: `1px solid ${outlineColor}`,
			background: "transparent",
		},
	}

	// Custom Scrollbar CSS using Material You tokens
	const scrollbarColors = {
		track: "transparent",
		thumb: onSurfaceVariant, // Use Material You subtle colors
		thumbHover: outlineColor,
	}
	const scrollbarClassNameTarget = `.ant-modal-wrap.mcc-modal-wrapper .mcc-modal-body-scroll-${mode}`
	const modalScrollbarCss = `
        ${scrollbarClassNameTarget} {
            scrollbar-width: thin;
            scrollbar-color: ${scrollbarColors.thumb} ${scrollbarColors.track};
        }
        ${scrollbarClassNameTarget}::-webkit-scrollbar { width: 6px; }
        ${scrollbarClassNameTarget}::-webkit-scrollbar-track { background: ${scrollbarColors.track}; border-radius: 3px; }
        ${scrollbarClassNameTarget}::-webkit-scrollbar-thumb { background: ${scrollbarColors.thumb}; border-radius: 3px; transition: background-color 0.2s ease; }
        ${scrollbarClassNameTarget}::-webkit-scrollbar-thumb:hover { background: ${scrollbarColors.thumbHover}; }
    `

	return (
		<>
			{/* Inject scrollbar styles dynamically */}
			<style>{modalScrollbarCss}</style>
			<AnimatePresence>
				{open && (
					<Modal
						open={open}
						onCancel={onClose}
						title={styledTitle}
						closable={false}
						width={width}
						zIndex={zIndex}
						styles={modalStyles}
						maskClosable={true}
						keyboard={true}
						footer={renderFooter()}
						centered
						wrapClassName="mcc-modal-wrapper"
					>
						<motion.div
							variants={modalContentContainerVariants}
							initial="hidden"
							animate="visible"
						>
							{message && (
								<motion.div variants={modalItemVariants}>
									<Typography.Paragraph
										style={{
											textAlign: "center",
											marginBottom: children ? 24 : 0,
											fontSize: 16,
											lineHeight: "1.7", // Slightly increased line height
											color: onSurfaceVariant, // Use Material You secondary text color
										}}
									>
										{message}
									</Typography.Paragraph>
								</motion.div>
							)}
							{children && (
								<motion.div variants={modalItemVariants}>{children}</motion.div>
							)}
						</motion.div>
					</Modal>
				)}
			</AnimatePresence>
		</>
	)
}
